
import { useState, useEffect } from "react";
import { Editor } from "@tiptap/react";
import { ImageIcon, AlignLeft, AlignCenter, AlignRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

interface ImageAlignmentControlsProps {
  editor: Editor;
}

export const ImageAlignmentControls = ({ editor }: ImageAlignmentControlsProps) => {
  const [hasSelectedImage, setHasSelectedImage] = useState(false);
  const [currentAlignment, setCurrentAlignment] = useState<'left' | 'center' | 'right'>('left');
  const [currentWidth, setCurrentWidth] = useState(50);

  useEffect(() => {
    if (!editor) return;

    const handleSelectionUpdate = () => {
      const { selection } = editor.state;
      
      // Check if current selection is an image node selection
      let imageFound = false;
      let imageAlignment: 'left' | 'center' | 'right' = 'left';
      let imageWidth = 50;

      // Check if we have a node selection and if it's an image
      if (selection && selection.from === selection.to && selection.$from.parent) {
        const node = selection.$from.nodeAfter;
        if (node && node.type.name === 'image') {
          imageFound = true;
          const style = node.attrs.style || '';
          
          // Extract alignment from style
          if (style.includes('float: right')) {
            imageAlignment = 'right';
          } else if (style.includes('margin: 0 auto') && style.includes('display: block')) {
            imageAlignment = 'center';
          } else {
            imageAlignment = 'left';
          }
          
          // Extract width from style
          const widthMatch = style.match(/width:\s*(\d+)%/);
          if (widthMatch) {
            imageWidth = parseInt(widthMatch[1]);
          }
          
          setCurrentAlignment(imageAlignment);
          setCurrentWidth(imageWidth);
        }
      }

      setHasSelectedImage(imageFound);
    };

    editor.on('selectionUpdate', handleSelectionUpdate);
    editor.on('transaction', handleSelectionUpdate);

    return () => {
      editor.off('selectionUpdate', handleSelectionUpdate);
      editor.off('transaction', handleSelectionUpdate);
    };
  }, [editor]);

  const getAlignmentStyle = (alignment: 'left' | 'center' | 'right', width: number) => {
    const baseStyle = `width: ${width}%; max-width: 100%; height: auto; border-radius: 8px; margin-bottom: 16px;`;
    
    switch (alignment) {
      case 'left':
        return `${baseStyle} float: left; margin-right: 16px; clear: left;`;
      case 'right':
        return `${baseStyle} float: right; margin-left: 16px; clear: right;`;
      case 'center':
        return `${baseStyle} display: block; margin: 0 auto 16px auto; clear: both;`;
      default:
        return `${baseStyle} float: left; margin-right: 16px; clear: left;`;
    }
  };

  const updateImageAlignment = (alignment: 'left' | 'center' | 'right') => {
    if (!hasSelectedImage) return;
    
    console.log("[ImageAlignmentControls] Updating alignment to:", alignment);
    
    const newStyle = getAlignmentStyle(alignment, currentWidth);
    editor.chain().focus().updateAttributes('image', { 
      style: newStyle,
      class: 'newsletter-image'
    }).run();
    setCurrentAlignment(alignment);
  };

  const updateImageWidth = (width: number) => {
    if (!hasSelectedImage) return;
    
    console.log("[ImageAlignmentControls] Updating width to:", width);
    
    const newStyle = getAlignmentStyle(currentAlignment, width);
    editor.chain().focus().updateAttributes('image', { 
      style: newStyle,
      class: 'newsletter-image'
    }).run();
    setCurrentWidth(width);
  };

  if (!hasSelectedImage) {
    return (
      <div className="p-4 bg-gray-50 border rounded-lg">
        <div className="flex items-center gap-2 text-gray-500">
          <ImageIcon className="h-4 w-4" />
          <span className="text-sm">Click on an image to adjust its alignment and size</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 bg-gray-50 border rounded-lg">
      <div className="flex items-center gap-2">
        <ImageIcon className="h-4 w-4" />
        <h3 className="text-sm font-medium">Image Controls</h3>
        <span className="text-xs text-green-600 font-medium">✓ Image Selected</span>
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium mb-2 block">Alignment</label>
          <div className="flex gap-2">
            <Button 
              variant={currentAlignment === 'left' ? 'default' : 'outline'} 
              size="sm"
              className="flex-1"
              onClick={() => updateImageAlignment('left')}
            >
              <AlignLeft className="h-4 w-4 mr-1" /> Left
            </Button>
            <Button 
              variant={currentAlignment === 'center' ? 'default' : 'outline'} 
              size="sm"
              className="flex-1"
              onClick={() => updateImageAlignment('center')}
            >
              <AlignCenter className="h-4 w-4 mr-1" /> Center
            </Button>
            <Button 
              variant={currentAlignment === 'right' ? 'default' : 'outline'} 
              size="sm"
              className="flex-1"
              onClick={() => updateImageAlignment('right')}
            >
              <AlignRight className="h-4 w-4 mr-1" /> Right
            </Button>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Width</label>
          <div className="flex items-center gap-4">
            <span className="text-xs text-gray-500">25%</span>
            <Slider
              value={[currentWidth]}
              min={25}
              max={100}
              step={25}
              onValueChange={(value) => updateImageWidth(value[0])}
              className="flex-1"
            />
            <span className="text-xs text-gray-500">100%</span>
          </div>
          <div className="text-xs text-center mt-1 text-gray-500">
            Current: {currentWidth}%
          </div>
        </div>
      </div>
    </div>
  );
};
